import { asNumber, basename, escapeRegExp, normalizePath, unique } from "./utils";

const ALLOWED_WEIGHTS = new Set([3, 7, 10]);
const SECRET_PATTERN =
  /(?:sk-[A-Za-z0-9_-]{16,}|e2b_[A-Za-z0-9]{16,}|gh[pousr]_[A-Za-z0-9]{16,})/g;

const requiredFiles = [
  "instruction.md",
  "task.toml",
  "environment/Dockerfile",
  "environment/requirements.txt",
  "solution/solve.sh",
  "tests/test.sh",
  "tests/finalize.py",
  "tests/graded/judge.toml",
  "tests/gating/gating.toml",
];

const issue = (severity, code, message, path = "") => ({
  severity,
  code,
  message,
  path,
});

const criterionMap = (criteria = []) =>
  new Map(criteria.map((item) => [String(item?.id || "").trim(), item]));

const normalizeTaskName = (name = "") =>
  String(name).split("/", 2).at(-1)?.toUpperCase().replace(/[^A-Z0-9]+/g, "-") || "";

const parseRegistrations = (files) => {
  const registrations = [];
  const pattern = /rk\.[A-Za-z_][A-Za-z0-9_]*\(\s*weight\s*=\s*([0-9.]+)\s*,\s*name\s*=\s*["']([^"']+)["']/g;
  for (const file of files.filter((entry) => /\/tests\/graded\/.*\.py$/.test(`/${entry.relative}`))) {
    let match;
    while ((match = pattern.exec(file.text || ""))) {
      registrations.push({
        id: match[2],
        weight: Number(match[1]),
        negate: false,
        kind: "programmatic",
        path: file.relative,
      });
    }
  }
  return registrations;
};

const configuredCriteria = (doc, kind) =>
  Array.isArray(doc?.criterion)
    ? doc.criterion.map((item) => ({ ...item, kind }))
    : [];

export const analyzeRequirements = ({
  sampleId,
  files,
  instruction,
  task,
  judge,
  gating,
  parseErrors = [],
  runs = [],
}) => {
  const issues = parseErrors.map((message) => issue("error", "parse_error", message));
  const byRelative = new Map(files.map((entry) => [normalizePath(entry.relative), entry]));

  for (const path of requiredFiles) {
    if (!byRelative.has(path)) {
      issues.push(issue("error", "required_file_missing", `缺少规范文件：${path}`, path));
    }
  }

  for (const forbidden of [".git/", "__pycache__/", ".venv/", "__MACOSX/", ".DS_Store", "reward.json", "reward-details.json"] ) {
    const found = files.find((entry) => entry.relative.includes(forbidden));
    if (found) {
      issues.push(issue("error", "residual_file", `题包内有禁止残留：${forbidden}`, found.relative));
    }
  }

  const instructionContract = [
    ["/app/input_files/", "说明只读输入目录 /app/input_files/"],
    ["/app/repo", "说明可写 Git 工作区 /app/repo"],
    ["/app/output/", "说明唯一交付目录 /app/output/"],
    ["/app/output/submission.patch", "逐字指定交付物 /app/output/submission.patch"],
  ].map(([marker, label]) => ({ marker, label, passed: instruction.includes(marker) }));

  const hasBinaryDiffExport =
    /git(?:\s+-C\s+\/app\/repo)?\s+diff\s+--binary/i.test(instruction) &&
    instruction.includes("submission.patch");
  instructionContract.push({
    marker: "git diff --binary",
    label: "给出从工作区导出 binary diff 的命令",
    passed: hasBinaryDiffExport,
    advisory: true,
  });

  for (const item of instructionContract) {
    if (!item.passed) {
      issues.push(issue(item.advisory ? "warning" : "error", "instruction_path_contract", `instruction.md 未${item.label}`, "instruction.md"));
    }
  }

  const metadata = task?.metadata || {};
  const taskConfig = task?.task || {};
  const deliverables = Array.isArray(metadata.deliverables) ? metadata.deliverables : [];
  const requiredDeliverables = deliverables.filter((item) => item?.required !== false);
  const artifacts = Array.isArray(task?.artifacts) ? task.artifacts : [];
  const expectedArtifacts = requiredDeliverables.map((item) => `/app/output/${item.path}`);

  if (task?.schema_version !== "1.4") {
    issues.push(issue("error", "schema_version", 'schema_version 必须为 "1.4"', "task.toml"));
  }
  if (!metadata.task_id || metadata.task_id !== sampleId) {
    issues.push(
      issue(
        "error",
        "task_id_mismatch",
        `目录名 ${sampleId} 与 metadata.task_id ${metadata.task_id || "(空)"} 不一致`,
        "task.toml",
      ),
    );
  }
  if (normalizeTaskName(taskConfig.name) !== sampleId) {
    issues.push(
      issue(
        "error",
        "task_name_mismatch",
        `[task].name 的 name 段与 ${sampleId} 不一致`,
        "task.toml",
      ),
    );
  }
  if (!String(taskConfig.name || "").startsWith("AlvanceAI/")) {
    issues.push(issue("error", "task_org", "[task].name 必须使用 AlvanceAI/ 前缀", "task.toml"));
  }
  const expectedKeyword = "coding";
  const keywords = Array.isArray(taskConfig.keywords) ? taskConfig.keywords : [];
  if (keywords[0] !== expectedKeyword || keywords[1] !== "office" || keywords[2] !== metadata.difficulty) {
    issues.push(
      issue(
        "error",
        "keywords",
        'keywords 应为 ["coding", "office", difficulty]',
        "task.toml",
      ),
    );
  }
  if (metadata.office_task_type !== "其他") {
    issues.push(issue("error", "office_task_type", "Coding 题 office_task_type 应为“其他”", "task.toml"));
  }
  if (metadata.vl_dependency !== "不依赖vl") {
    issues.push(issue("error", "vl_dependency", "非视觉 Coding 题 vl_dependency 应为“不依赖vl”", "task.toml"));
  }
  if (JSON.stringify(artifacts) !== JSON.stringify(expectedArtifacts)) {
    issues.push(issue("error", "artifacts_mismatch", "artifacts 与必交 deliverables 不一致", "task.toml"));
  }
  for (const deliverable of requiredDeliverables) {
    const path = String(deliverable.path || "");
    if (!path || /[*?[\]]/.test(path)) {
      issues.push(issue("error", "deliverable_path", "必交 deliverable 必须是精确文件名", "task.toml"));
      continue;
    }
    for (const location of ["solution/golden_output", "tests/golden_output"]) {
      if (!byRelative.has(`${location}/${path}`)) {
        issues.push(issue("error", "golden_missing", `${location}/${path} 不存在`, location));
      }
    }
    if (!instruction.includes(path)) {
      issues.push(issue("error", "instruction_deliverable", `instruction.md 未逐字写出 ${path}`, "instruction.md"));
    }
  }

  const judgeFiles = Array.isArray(judge?.judge?.files) ? judge.judge.files : [];
  const expectedJudgeFiles = ["/app/output/submission.patch", "/tests/golden_output/submission.patch"];
  for (const expected of expectedJudgeFiles) {
    if (!judgeFiles.includes(expected)) {
      issues.push(issue("error", "judge_files", `graded Judge files 缺少 ${expected}`, "tests/graded/judge.toml"));
    }
  }
  const goldenA = byRelative.get("solution/golden_output/submission.patch")?.text;
  const goldenB = byRelative.get("tests/golden_output/submission.patch")?.text;
  if (goldenA !== undefined && goldenB !== undefined && goldenA !== goldenB) {
    issues.push(issue("error", "golden_mismatch", "solution 与 tests 的 golden submission.patch 不一致", "golden_output"));
  }

  const verifier = task?.verifier || {};
  const verifierEnv = verifier.env || task?.["verifier.env"] || {};
  const environment = task?.environment || {};
  if (asNumber(task?.agent?.timeout_sec, 0) < 72000) {
    issues.push(issue("error", "agent_timeout", "agent.timeout_sec 必须至少 72000", "task.toml"));
  }
  if (asNumber(verifier.timeout_sec, 0) < 18000) {
    issues.push(issue("error", "verifier_timeout", "verifier.timeout_sec 必须至少 18000", "task.toml"));
  }
  if (verifier.network_mode !== "allowlist" || !(verifier.allowed_hosts || []).includes("${JUDGE_GATEWAY}")) {
    issues.push(issue("error", "verifier_network", "verifier 必须 allowlist ${JUDGE_GATEWAY}", "task.toml"));
  }
  for (const key of ["OPENAI_API_KEY", "OPENAI_BASE_URL"]) {
    if (verifierEnv[key] !== `\${${key}}`) {
      issues.push(issue("error", "verifier_env", `verifier.env.${key} 必须使用占位符`, "task.toml"));
    }
  }
  if (environment.network_mode !== "no-network") {
    issues.push(issue("warning", "environment_network", "Coding 快照通常应使用 no-network", "task.toml"));
  }

  const docker = byRelative.get("environment/Dockerfile")?.text || "";
  for (const [marker, label] of [
    ['"harbor-rewardkit[all]==0.1.7"', "固定 RewardKit 版本"],
    ["/app/input_files", "只读输入位置"],
    ["/app/repo", "可写仓库"],
    ["git -C /app/repo init", "Git baseline 初始化"],
    ["WORKDIR /app", "固定工作目录"],
  ]) {
    if (!docker.includes(marker)) {
      issues.push(issue("error", "docker_contract", `Dockerfile 缺少${label}：${marker}`, "environment/Dockerfile"));
    }
  }
  const requirements = byRelative.get("environment/requirements.txt")?.text || "";
  if (!/pytest/i.test(`${docker}\n${requirements}`)) {
    issues.push(issue("error", "pytest_missing", "Agent 镜像未安装 pytest", "environment/requirements.txt"));
  }
  const codingVerify = byRelative.get("tests/coding_verify.py")?.text || "";
  if (codingVerify) {
    if (/task\.toml/i.test(codingVerify)) {
      issues.push(issue("error", "coding_verify_task_toml", "coding_verify.py 不应读取 task.toml（Harbor 容器不挂载）", "tests/coding_verify.py"));
    }
    for (const marker of ["input_files/repository", "output/submission.patch"]) {
      if (!codingVerify.includes(marker)) {
        issues.push(issue("error", "coding_verify_path", `coding_verify.py 缺少固定路径 ${marker}`, "tests/coding_verify.py"));
      }
    }
  }

  const judgeCriteria = configuredCriteria(judge, "judge");
  const gateCriteria = configuredCriteria(gating, "gate");
  const programmaticCriteria = parseRegistrations(files);
  const allCriteria = [...judgeCriteria, ...gateCriteria, ...programmaticCriteria];
  const rubricIndex = Array.isArray(metadata.rubric_index) ? metadata.rubric_index : [];
  const indexById = criterionMap(rubricIndex);
  const configuredIds = unique([...judgeCriteria, ...gateCriteria].map((item) => String(item.id || "")));
  const indexedIds = unique(rubricIndex.map((item) => String(item.id || "")));

  if (configuredIds.sort().join("|") !== indexedIds.sort().join("|")) {
    issues.push(issue("error", "rubric_index_mismatch", "Judge/Gate criterion ID 与 rubric_index 不完全一致", "task.toml"));
  }
  for (const criterion of [...judgeCriteria, ...programmaticCriteria]) {
    const weight = asNumber(criterion.weight);
    if (!ALLOWED_WEIGHTS.has(weight)) {
      issues.push(
        issue(
          "error",
          "weight_value",
          `${criterion.id || "未知 criterion"} 的 weight=${criterion.weight}，只允许 3/7/10`,
          criterion.path || "tests/graded/judge.toml",
        ),
      );
    }
    if (criterion.type && criterion.type !== "binary") {
      issues.push(issue("error", "criterion_type", `${criterion.id} 只允许 binary`, "tests/graded/judge.toml"));
    }
    const indexed = indexById.get(String(criterion.id || ""));
    if (indexed && asNumber(indexed.weight) !== weight) {
      issues.push(issue("error", "rubric_weight_mismatch", `${criterion.id} 的 rubric_index 权重不一致`, "task.toml"));
    }
  }
  for (const criterion of gateCriteria) {
    if (criterion.weight !== undefined) {
      issues.push(issue("error", "gate_weight", `${criterion.id} 是 gate，不应写 weight`, "tests/gating/gating.toml"));
    }
    if (criterion.negate !== true || criterion.type !== "binary") {
      issues.push(issue("error", "gate_shape", `${criterion.id} 必须 binary + negate=true`, "tests/gating/gating.toml"));
    }
  }
  if (judge?.judge?.judge !== "openai/gpt-5.5") {
    issues.push(issue("error", "judge_model", "graded Judge 必须为 openai/gpt-5.5", "tests/graded/judge.toml"));
  }
  if (gating?.judge?.judge !== "openai/gpt-5.5") {
    issues.push(issue("error", "gate_judge_model", "gating Judge 必须为 openai/gpt-5.5", "tests/gating/gating.toml"));
  }
  if (judge?.scoring?.aggregation !== "weighted_mean") {
    issues.push(issue("error", "judge_aggregation", "graded aggregation 必须为 weighted_mean", "tests/graded/judge.toml"));
  }
  if (gating?.scoring?.aggregation !== "all_pass") {
    issues.push(issue("error", "gate_aggregation", "gating aggregation 必须为 all_pass", "tests/gating/gating.toml"));
  }

  const positiveJudgeWeight = judgeCriteria
    .filter((item) => item.negate !== true)
    .reduce((sum, item) => sum + (asNumber(item.weight, 0) || 0), 0);
  const positiveProgrammaticWeight = programmaticCriteria.reduce(
    (sum, item) => sum + (asNumber(item.weight, 0) || 0),
    0,
  );
  const totalPositive = positiveJudgeWeight + positiveProgrammaticWeight;
  const programmaticShare = totalPositive ? positiveProgrammaticWeight / totalPositive : 0;
  if (programmaticShare > 0.2) {
    issues.push(
      issue(
        "error",
        "programmatic_share",
        `程序化正向权重占 ${(programmaticShare * 100).toFixed(1)}%，超过 20%`,
        "tests/graded",
      ),
    );
  }

  const criticalCount = judgeCriteria.filter((item) => asNumber(item.weight) === 10 && item.negate !== true).length;
  const negativeCount = judgeCriteria.filter((item) => item.negate === true).length;
  const rubricCount = judgeCriteria.length + gateCriteria.length;
  const minimumByDifficulty = { L2: 8, L3: 12, L4: 18, L5: 25 };
  const minimum = minimumByDifficulty[String(metadata.difficulty || "").toUpperCase()] || 0;
  if (rubricCount < minimum) {
    issues.push(issue("error", "rubric_count", `${metadata.difficulty} 需至少 ${minimum} 条 Judge+Gate，当前 ${rubricCount}`, "tests"));
  }
  if (criticalCount < 2) {
    issues.push(issue("error", "critical_count", "至少需要 2 条正向 weight=10 rubric", "tests/graded/judge.toml"));
  }
  if (rubricCount && negativeCount / rubricCount < 0.2) {
    issues.push(issue("warning", "negative_ratio", "负向 graded rubric 少于总 Judge+Gate 的 20%", "tests/graded/judge.toml"));
  }
  const anchorWeight = rubricIndex
    .filter(
      (item) =>
        ["交付物内容质量-准确性", "交付物内容质量-专业性"].includes(item.dimension) &&
        item.negate !== true &&
        item.gating !== true,
    )
    .reduce((sum, item) => sum + (asNumber(item.weight, 0) || 0), 0);
  const anchorRatio = positiveJudgeWeight ? anchorWeight / positiveJudgeWeight : 0;
  if (anchorRatio < 0.3) {
    issues.push(issue("error", "anchor_ratio", `领域锚点正分占 ${(anchorRatio * 100).toFixed(1)}%，低于 30%`, "task.toml"));
  }

  const secretHits = [];
  for (const file of files.filter((entry) => entry.kind === "text")) {
    for (const match of file.text.matchAll(SECRET_PATTERN)) {
      secretHits.push({ path: file.relative, preview: `${match[0].slice(0, 6)}…${match[0].slice(-4)}` });
    }
  }
  if (secretHits.length) {
    issues.push(issue("error", "secret_detected", `发现 ${secretHits.length} 个疑似真实凭证`, secretHits[0].path));
  }

  const scoreIssues = [];
  for (const run of runs) {
    if (run.agentStatus !== "completed" || (run.agentExitCode !== null && run.agentExitCode !== 0)) {
      issues.push(
        issue(
          "error",
          "rollout_agent_failed",
          `${run.modelLabel} solver 未完成：agent_status=${run.agentStatus}, exit=${run.agentExitCode ?? "—"}`,
          "rollouts",
        ),
      );
    }
    if (run.verifierError) {
      issues.push(issue("error", "rollout_verifier_error", `${run.modelLabel} verifier_error=${run.verifierError}`, "rollouts"));
    }
    if (run.gatingUnavailable) {
      issues.push(issue("error", "rollout_gate_unavailable", `${run.modelLabel} gating_unavailable=${run.gatingUnavailable}`, "rollouts"));
    }
    if (run.taskTreeStable === false) {
      issues.push(issue("error", "rollout_hash_stale", `${run.modelLabel} rollout 后 task tree 发生变化`, "rollouts"));
    }
    const effective = asNumber(run.effectiveScore);
    if (effective === null) continue;
    if (effective >= 0.5) scoreIssues.push(`${run.modelLabel} effective=${effective.toFixed(3)} ≥ 0.5`);
    else if (effective < 0.3) scoreIssues.push(`${run.modelLabel} effective=${effective.toFixed(3)} 低于偏好区间 0.3`);
  }
  if (scoreIssues.some((value) => value.includes("≥"))) {
    issues.push(issue("error", "model_score_high", scoreIssues.filter((value) => value.includes("≥")).join("；"), "rollouts"));
  }
  if (scoreIssues.some((value) => value.includes("低于"))) {
    issues.push(issue("warning", "model_score_low", scoreIssues.filter((value) => value.includes("低于")).join("；"), "rollouts"));
  }
  const roster = new Set(runs.map((run) => run.modelLabel));
  for (const model of ["GPT-5.6-sol", "Opus 4.8"]) {
    if (!roster.has(model)) {
      issues.push(issue("error", "rollout_missing", `缺少 ${model} 的新鲜完整 rollout`, "rollouts"));
    }
  }

  const duplicateDescriptions = [];
  const seenDescriptions = new Map();
  for (const criterion of [...judgeCriteria, ...gateCriteria]) {
    const normalized = String(criterion.description || "").trim().toLowerCase();
    if (!normalized) continue;
    if (seenDescriptions.has(normalized)) duplicateDescriptions.push([seenDescriptions.get(normalized), criterion.id]);
    else seenDescriptions.set(normalized, criterion.id);
  }
  if (duplicateDescriptions.length) {
    issues.push(issue("warning", "rubric_duplicate", `存在重复 rubric：${duplicateDescriptions.map((ids) => ids.join("/" )).join("、")}`, "tests"));
  }

  const errors = issues.filter((item) => item.severity === "error").length;
  const warnings = issues.filter((item) => item.severity === "warning").length;
  return {
    passed: errors === 0,
    issues,
    errors,
    warnings,
    instructionContract,
    secretHits,
    metrics: {
      rubricCount,
      judgeCount: judgeCriteria.length,
      gateCount: gateCriteria.length,
      programmaticCount: programmaticCriteria.length,
      criticalCount,
      negativeCount,
      positiveJudgeWeight,
      positiveProgrammaticWeight,
      programmaticShare,
      anchorWeight,
      anchorRatio,
    },
    criteria: allCriteria.map((item) => ({
      ...item,
      metadata: indexById.get(String(item.id || "")) || null,
    })),
  };
};

export const criterionCoverage = (instruction, criteria) =>
  criteria.map((criterion) => {
    const description = String(criterion.description || "");
    const tokens = unique(
      description.match(/[A-Za-z][A-Za-z0-9_.-]{3,}|[\u4e00-\u9fff]{2,8}|\d+(?:\.\d+)?/g) || [],
    ).filter((token) => !["candidate", "patch", "交付物", "任务", "文件"].includes(token.toLowerCase()));
    const hits = tokens.filter((token) => new RegExp(escapeRegExp(token), "i").test(instruction));
    return { ...criterion, anchorTokens: hits.slice(0, 6), anchored: hits.length > 0 };
  });
